import React, { useCallback, useEffect, useMemo, useState, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, Users, Calendar, CreditCard, Sparkles } from 'lucide-react';
import { PlatformCheckoutShell } from '../../../components/auth/PlatformCheckoutShell';
import { AcademyOnboardingWizard } from '../../../components/onboarding/AcademyOnboardingWizard';
import { OnboardingStepActionModal } from '../../../components/onboarding/OnboardingStepActionModal';
import { Button } from '@/components/ui';
import { useAuthStore } from '../../../store/auth';
import { useAcademies } from '../../../hooks/useDataStore';
import { academyService } from '../../../services/academy';
import {
  trackPostSubscriptionOnboardingComplete,
  trackPostSubscriptionOnboardingStep,
} from '../../../utils/gtmTracking';
import { FEATURE_FLAG_KEYS, type AcademyOnboardingProgressInput, getPostSubscriptionStepCopy } from '@court-sync/shared';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { tryAutoAddOwnerAsCoach } from '../../../lib/selfCoachPrompt';

import { OrganizationPublicSiteEmbed } from '@/components/organization/OrganizationPublicSiteEmbed';
const CourtManagement = lazy(() => import('./courts/CourtManagement'));
const CoachManagement = lazy(() => import('./staff/CoachManagement'));
const TermManagement = lazy(() => import('./terms/TermManagement'));
const OwnerCalendar = lazy(() => import('./calendar/Calendar'));
const PaymentManagement = lazy(() => import('./payments/PaymentManagement'));

type OnboardingActionModalId =
  | 'branding'
  | 'courts'
  | 'team'
  | 'terms'
  | 'calendar'
  | 'payments';

const ONBOARDING_MODAL_TITLES: Record<OnboardingActionModalId, string> = {
  branding: 'Academy settings',
  courts: 'Courts',
  team: 'Coaches',
  terms: 'Terms',
  calendar: 'Calendar',
  payments: 'Payment settings',
};

const POST_SUBSCRIPTION_STEPS = [
  { id: 'welcome', title: 'Welcome to CourtSync', description: 'A few quick steps to get your academy ready.' },
  { id: 'branding', title: 'Make it yours', description: 'Add branding and contact details families will see.' },
  { id: 'courts', title: 'Add courts', description: 'Courts power scheduling across your programs.' },
  { id: 'team', title: 'Build your team', description: 'Invite coaches if others help you run lessons.' },
  { id: 'programs', title: 'Publish programs', description: 'Create terms and lessons when you are ready.' },
  { id: 'payments', title: 'Set up payments', description: 'Connect Stripe or add manual payment instructions.' },
] as const;

function shouldShowTeamStep(profile: ReturnType<typeof useAcademies>['currentAcademy']): boolean {
  const operatingProfile = profile?.operatingProfile;
  if (!operatingProfile) return true;
  if (operatingProfile.operatingRole === 'solo_coach') return false;
  if (operatingProfile.coachCountBand === '0') return false;
  return true;
}

function shouldShowPaymentsStep(
  profile: ReturnType<typeof useAcademies>['currentAcademy'],
  hasPaymentFeature: boolean
): boolean {
  if (!hasPaymentFeature) return true;
  const workflows = profile?.operatingProfile?.desiredWorkflows ?? [];
  return profile?.operatingProfile?.needsOnlinePayments === true || workflows.includes('online_payments');
}

export default function OwnerPostSubscriptionOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { enabled: postSubscriptionOnboardingEnabled, loading: flagLoading } = useFeatureFlag(
    FEATURE_FLAG_KEYS.POST_SUBSCRIPTION_ONBOARDING
  );
  const { currentAcademy, loadAcademy } = useAcademies();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [openModal, setOpenModal] = useState<OnboardingActionModalId | null>(null);

  const closeModal = useCallback(() => setOpenModal(null), []);

  useEffect(() => {
    if (user?.academyId) {
      void loadAcademy(user.academyId);
    }
  }, [loadAcademy, user?.academyId]);

  useEffect(() => {
    if (flagLoading || postSubscriptionOnboardingEnabled) return;
    navigate('/admin/dashboard', { replace: true });
  }, [flagLoading, navigate, postSubscriptionOnboardingEnabled]);

  const visibleSteps = useMemo(() => {
    const showTeam = shouldShowTeamStep(currentAcademy);
    const showPayments = shouldShowPaymentsStep(currentAcademy, true);
    return POST_SUBSCRIPTION_STEPS.filter((step) => {
      if (step.id === 'team') return showTeam;
      if (step.id === 'payments') return showPayments;
      return true;
    });
  }, [currentAcademy]);

  const persistProgress = useCallback(
    async (progress: Partial<AcademyOnboardingProgressInput>, completed = false) => {
      if (!user?.academyId) return;
      const payload: AcademyOnboardingProgressInput = {
        version: 1,
        currentStepId: progress.currentStepId ?? visibleSteps[currentStepIndex]?.id ?? 'welcome',
        completedStepIds: progress.completedStepIds ?? [],
        skippedAt: progress.skippedAt ?? null,
        completedAt: completed ? new Date().toISOString() : progress.completedAt ?? null,
      };
      try {
        await academyService.updateAcademy(user.academyId, { onboardingProgress: payload });
      } catch {
        // Non-blocking — owner can continue either way
      }
    },
    [currentStepIndex, user?.academyId, visibleSteps]
  );

  const finishOnboarding = async (skipped: boolean) => {
    setIsSaving(true);
    try {
      await persistProgress(
        {
          currentStepId: visibleSteps[currentStepIndex]?.id,
          completedStepIds: visibleSteps.map((step) => step.id),
          skippedAt: skipped ? new Date().toISOString() : null,
        },
        !skipped
      );

      if (user?.academyId && currentAcademy?.operatingProfile) {
        await tryAutoAddOwnerAsCoach({
          academyId: user.academyId,
          user,
          operatingProfile: currentAcademy.operatingProfile,
        });
      }
    } finally {
      setIsSaving(false);
      trackPostSubscriptionOnboardingComplete({
        skipped,
        completedStepIds: visibleSteps.map((step) => step.id),
      });
      navigate('/admin/dashboard', {
        replace: true,
        state: { message: skipped ? 'You can finish setup anytime from your dashboard.' : 'Welcome aboard!' },
      });
    }
  };

  const currentStep = visibleSteps[currentStepIndex];

  useEffect(() => {
    if (!currentStep) return;
    trackPostSubscriptionOnboardingStep({
      stepId: currentStep.id,
      stepNumber: currentStepIndex + 1,
      totalSteps: visibleSteps.length,
    });
  }, [currentStep, currentStepIndex, visibleSteps.length]);

  const stepContent = useMemo(() => {
    const profile = currentAcademy?.operatingProfile;
    switch (currentStep?.id) {
      case 'welcome':
        return (
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Your subscription is active. These steps are optional — skip anytime and use the getting-started checklist on your dashboard.
          </p>
        );
      case 'branding':
        return (
          <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
            <p>Upload your logo, add a description, and review notification defaults.</p>
            <Button type="button" variant="outline" onClick={() => setOpenModal('branding')}>
              Open academy settings
            </Button>
          </div>
        );
      case 'courts':
        return (
          <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
            <p>Add at least one court so lessons can be scheduled.</p>
            <Button type="button" variant="outline" onClick={() => setOpenModal('courts')}>
              Add courts
            </Button>
          </div>
        );
      case 'team':
        return (
          <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
            <p>
              {getPostSubscriptionStepCopy('team', profile) ??
                'Invite coaches to manage their own schedules and attendance.'}
            </p>
            <Button type="button" variant="outline" onClick={() => setOpenModal('team')}>
              Manage staff
            </Button>
          </div>
        );
      case 'programs':
        return (
          <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
            <p>
              {getPostSubscriptionStepCopy('programs', profile) ??
                'Create a term and your first lessons when you are ready. On Starter, families browse your public catalogue and submit booking requests — you confirm enrollment from the owner dashboard.'}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setOpenModal('terms')}>
                Create a term
              </Button>
              <Button type="button" variant="outline" onClick={() => setOpenModal('calendar')}>
                Open calendar
              </Button>
            </div>
          </div>
        );
      case 'payments':
        return (
          <div className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
            <p>
              {getPostSubscriptionStepCopy('payments', profile) ??
                'Set up Stripe Connect or add instructions for manual payments.'}
            </p>
            <Button type="button" variant="outline" onClick={() => setOpenModal('payments')}>
              Payment settings
            </Button>
          </div>
        );
      default:
        return null;
    }
  }, [currentAcademy?.operatingProfile, currentStep?.id]);

  const leftPanel = (
    <>
      <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900">
        <Sparkles className="h-6 w-6 text-primary-600" aria-hidden />
      </div>
      <h1 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
        Activate your academy
      </h1>
      <p className="mb-6 text-gray-600 dark:text-gray-400">
        Optional setup based on how you told us you operate. Nothing here blocks your dashboard.
      </p>
      <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
        <li className="flex gap-2">
          <Building2 className="h-4 w-4 shrink-0 text-primary-600" aria-hidden />
          <span>Branding and public academy page</span>
        </li>
        <li className="flex gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
          <span>Courts and locations</span>
        </li>
        <li className="flex gap-2">
          <Users className="h-4 w-4 shrink-0 text-blue-600" aria-hidden />
          <span>Coaches and programs</span>
        </li>
        <li className="flex gap-2">
          <Calendar className="h-4 w-4 shrink-0 text-orange-600" aria-hidden />
          <span>Terms, lessons, and calendar</span>
        </li>
        <li className="flex gap-2">
          <CreditCard className="h-4 w-4 shrink-0 text-purple-600" aria-hidden />
          <span>Payments when you are ready</span>
        </li>
      </ul>
    </>
  );

  if (flagLoading || !postSubscriptionOnboardingEnabled) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (!currentStep) return null;

  const isLastStep = currentStepIndex >= visibleSteps.length - 1;

  return (
    <PlatformCheckoutShell asideVariant="onboarding" left={leftPanel}>
      <div className="mx-auto w-full max-w-2xl">
        <AcademyOnboardingWizard
          steps={visibleSteps}
          currentStepIndex={currentStepIndex}
          onBack={() => setCurrentStepIndex((index) => Math.max(0, index - 1))}
          onNext={() => {
            if (isLastStep) {
              void finishOnboarding(false);
              return;
            }
            setCurrentStepIndex((index) => index + 1);
          }}
          nextLabel={isLastStep ? 'Go to dashboard' : 'Continue'}
          isSubmitting={isSaving}
          footerExtra={
            <Button
              type="button"
              variant="ghost"
              className="w-full sm:w-auto"
              disabled={isSaving}
              onClick={() => void finishOnboarding(true)}
            >
              Skip for now
            </Button>
          }
        >
          {stepContent}
        </AcademyOnboardingWizard>
      </div>

      {openModal ? (
        <OnboardingStepActionModal
          open
          onOpenChange={(open) => !open && closeModal()}
          title={ONBOARDING_MODAL_TITLES[openModal]}
        >
          {openModal === 'branding' ? (
            <OrganizationPublicSiteEmbed onEmbeddedClose={closeModal} />
          ) : null}
          {openModal === 'courts' ? <CourtManagement embedded onEmbeddedClose={closeModal} /> : null}
          {openModal === 'team' ? <CoachManagement embedded onEmbeddedClose={closeModal} /> : null}
          {openModal === 'terms' ? <TermManagement embedded onEmbeddedClose={closeModal} /> : null}
          {openModal === 'calendar' ? <OwnerCalendar embedded onEmbeddedClose={closeModal} /> : null}
          {openModal === 'payments' ? (
            <PaymentManagement embedded defaultTab="settings" onEmbeddedClose={closeModal} />
          ) : null}
        </OnboardingStepActionModal>
      ) : null}
    </PlatformCheckoutShell>
  );
}
